import React, { useState, useEffect, useRef, useCallback } from 'react';
import AdminLayout from '../../components/Layout/AdminLayout';
import { api } from '../../services/api';
import type { ChamberRegistryEntry } from '@vedjegy/shared';

type EditState = {
  taxId: string; email: string; email2: string; isRenewal: boolean;
  companyName: string; hqZip: string; hqCity: string; hqStreet: string;
  memberSince: string; chapter: string; region: string;
  saving: boolean; error: string;
};

export default function AdminChamberRegistryPage() {
  const [entries, setEntries]     = useState<ChamberRegistryEntry[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number; details: string[] } | null>(null);
  const [search, setSearch]       = useState('');
  const [editId, setEditId]       = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState>({
    taxId: '', email: '', email2: '', isRenewal: false, companyName: '', hqZip: '', hqCity: '', hqStreet: '',
    memberSince: '', chapter: '', region: '', saving: false, error: '',
  });
  const fileInputRef              = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.chamberRegistry.list();
      setEntries(data.entries);
    } catch {
      setError('Nem sikerült betölteni a nyilvántartást.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    setError('');
    try {
      const result = await api.chamberRegistry.import(file);
      setImportResult({ imported: result.imported, skipped: result.skipped, details: result.skippedDetails });
      await load();
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? 'Import sikertelen.');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  function startEdit(entry: ChamberRegistryEntry) {
    setEditId(entry.id);
    setEditState({
      taxId:       entry.taxId,
      email:       entry.email,
      email2:      entry.email2       ?? '',
      isRenewal:   entry.isRenewal,
      companyName: entry.companyName  ?? '',
      hqZip:       entry.hqZip        ?? '',
      hqCity:      entry.hqCity       ?? '',
      hqStreet:    entry.hqStreet     ?? '',
      memberSince: entry.memberSince ? entry.memberSince.slice(0, 10) : '',
      chapter:     entry.chapter      ?? '',
      region:      entry.region       ?? '',
      saving: false, error: '',
    });
  }

  function cancelEdit() { setEditId(null); }

  async function saveEdit(id: string) {
    setEditState((s) => ({ ...s, saving: true, error: '' }));
    try {
      await api.chamberRegistry.patch(id, {
        taxId:       editState.taxId,
        email:       editState.email,
        email2:      editState.email2       || null,
        isRenewal:   editState.isRenewal,
        companyName: editState.companyName  || null,
        hqZip:       editState.hqZip        || null,
        hqCity:      editState.hqCity       || null,
        hqStreet:    editState.hqStreet     || null,
        memberSince: editState.memberSince  || null,
        chapter:     editState.chapter      || null,
        region:      editState.region       || null,
      });
      setEntries((prev) => prev.map((e) => e.id === id ? {
        ...e,
        taxId:       editState.taxId,
        email:       editState.email,
        email2:      editState.email2       || null,
        isRenewal:   editState.isRenewal,
        companyName: editState.companyName  || null,
        hqZip:       editState.hqZip        || null,
        hqCity:      editState.hqCity       || null,
        hqStreet:    editState.hqStreet     || null,
        memberSince: editState.memberSince  || null,
        chapter:     editState.chapter      || null,
        region:      editState.region       || null,
      } : e));
      setEditId(null);
    } catch (err: unknown) {
      setEditState((s) => ({ ...s, saving: false, error: (err as { message?: string })?.message ?? 'Mentés sikertelen.' }));
    }
  }

  async function handleDelete(id: string, taxId: string) {
    if (!confirm(`Törli a(z) ${taxId} adószámú bejegyzést?`)) return;
    try {
      await api.chamberRegistry.delete(id);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      if (editId === id) setEditId(null);
    } catch {
      setError('Törlés sikertelen.');
    }
  }

  const q = search.toLowerCase();
  const filtered = entries.filter((e) =>
    !search ||
    e.taxId.includes(search) ||
    e.email.toLowerCase().includes(q) ||
    (e.email2      ?? '').toLowerCase().includes(q) ||
    (e.companyName ?? '').toLowerCase().includes(q) ||
    (e.chapter     ?? '').toLowerCase().includes(q) ||
    (e.region      ?? '').toLowerCase().includes(q),
  );

  const inp = (w?: number): React.CSSProperties => ({
    width: w ?? '100%', padding: '4px 7px', border: '1px solid #d1d5db',
    borderRadius: 5, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit',
  });
  const td: React.CSSProperties = { padding: '6px 12px', verticalAlign: 'middle', fontSize: 13 };
  const th: React.CSSProperties = { ...td, padding: '8px 12px', fontWeight: 700, color: '#6b7280', borderBottom: '2px solid #e5e7eb', background: '#f9fafb' };

  return (
    <AdminLayout>
      <div style={{ padding: 28, maxWidth: 2000 }}>
        <h1 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700 }}>Kamarai nyilvántartás</h1>
        <p style={{ margin: '0 0 24px', fontSize: 13, color: '#6b7280' }}>
          Regisztrációkor csak az itt szereplő adószám–email párok fogadhatók el.
          Sorban kattintva szerkeszthető, vagy Excel-ből tömegesen importálható.
        </p>

        <div style={{ marginBottom: 24, padding: 20, borderRadius: 8, border: '2px dashed #d1d5db', background: '#f9fafb', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Excel feltöltés</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              .xlsx fájl — kötelező: <code>adószám</code>, <code>email</code><br />
              opcionális: <code>cégnév</code>, <code>irányítószám</code>, <code>város</code>, <code>utca</code>, <code>belépés</code>, <code>tagozat</code>, <code>térség</code>
            </div>
          </div>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFileChange} />
          <button onClick={() => fileInputRef.current?.click()} disabled={importing}
            style={{ padding: '9px 22px', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: importing ? 'default' : 'pointer', opacity: importing ? 0.6 : 1 }}>
            {importing ? 'Importálás…' : 'Fájl kiválasztása'}
          </button>
          {importResult && (
            <div style={{ padding: '8px 14px', borderRadius: 6, fontSize: 13,
              background: importResult.skipped > 0 ? '#fffbeb' : '#f0fdf4',
              border: `1px solid ${importResult.skipped > 0 ? '#fde68a' : '#bbf7d0'}`,
              color: importResult.skipped > 0 ? '#92400e' : '#166534' }}>
              ✓ {importResult.imported} sor importálva
              {importResult.skipped > 0 && ` · ${importResult.skipped} kihagyva`}
              {importResult.details.length > 0 && <div style={{ marginTop: 4, fontSize: 11 }}>{importResult.details.slice(0, 5).join(', ')}</div>}
            </div>
          )}
        </div>

        {error && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, color: '#dc2626', fontSize: 13 }}>
            {error}
            <button onClick={() => setError('')} style={{ marginLeft: 10, fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <input placeholder="Keresés adószám vagy email alapján…" value={search} onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
          <span style={{ fontSize: 13, color: '#6b7280', whiteSpace: 'nowrap' }}>
            {filtered.length} / {entries.length} bejegyzés
          </span>
        </div>

        {loading ? (
          <div style={{ color: '#9ca3af', fontSize: 14 }}>Betöltés…</div>
        ) : (
          <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e5e7eb' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 100 }}>Adószám</th>
                  <th style={th}>Cégnév</th>
                  <th style={th}>E-mail</th>
                  <th style={th}>E-mail 2</th>
                  <th style={{ ...th, width: 80, textAlign: 'center' }}>Megújító</th>
                  <th style={{ ...th, width: 110 }}>Tagozat</th>
                  <th style={{ ...th, width: 110 }}>Térség</th>
                  <th style={{ ...th, width: 100 }}>Belépés</th>
                  <th style={{ ...th, width: 120 }}>Importálva</th>
                  <th style={{ ...th, width: 150 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: '#9ca3af', padding: 24 }}>Nincs bejegyzés.</td></tr>
                )}
                {filtered.map((e) => {
                  const isEditing = editId === e.id;
                  return (
                    <React.Fragment key={e.id}>
                      <tr
                        style={{ borderBottom: isEditing ? 'none' : '1px solid #f3f4f6', background: isEditing ? '#eff6ff' : '#fff' }}
                        title={isEditing ? undefined : 'Szerkesztés gomb, vagy dupla kattintás'}
                        onDoubleClick={() => !isEditing && startEdit(e)}
                      >
                        <td style={td}>
                          {isEditing
                            ? <input style={{ ...inp(84), fontFamily: 'monospace' }} value={editState.taxId} maxLength={8}
                                onChange={(ev) => setEditState((s) => ({ ...s, taxId: ev.target.value }))} />
                            : <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{e.taxId}</span>}
                        </td>
                        <td style={td}>
                          {isEditing
                            ? <input style={inp()} value={editState.companyName}
                                onChange={(ev) => setEditState((s) => ({ ...s, companyName: ev.target.value }))} />
                            : <span style={{ color: e.companyName ? undefined : '#9ca3af' }}>{e.companyName ?? '—'}</span>}
                        </td>
                        <td style={td}>
                          {isEditing
                            ? <input style={inp()} type="email" value={editState.email}
                                onChange={(ev) => setEditState((s) => ({ ...s, email: ev.target.value }))} />
                            : e.email}
                        </td>
                        <td style={td}>
                          {isEditing
                            ? <input style={inp()} type="email" value={editState.email2}
                                placeholder="opcionális"
                                onChange={(ev) => setEditState((s) => ({ ...s, email2: ev.target.value }))} />
                            : <span style={{ color: e.email2 ? undefined : '#9ca3af' }}>{e.email2 ?? '—'}</span>}
                        </td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          {isEditing
                            ? <input type="checkbox" checked={editState.isRenewal}
                                onChange={(ev) => setEditState((s) => ({ ...s, isRenewal: ev.target.checked }))} />
                            : e.isRenewal
                              ? <span style={{ color: '#16a34a', fontWeight: 700 }}>✓</span>
                              : <span style={{ color: '#9ca3af' }}>—</span>}
                        </td>
                        <td style={td}>
                          {isEditing
                            ? <input style={inp()} value={editState.chapter}
                                onChange={(ev) => setEditState((s) => ({ ...s, chapter: ev.target.value }))} />
                            : <span style={{ color: e.chapter ? undefined : '#9ca3af' }}>{e.chapter ?? '—'}</span>}
                        </td>
                        <td style={td}>
                          {isEditing
                            ? <input style={inp()} value={editState.region}
                                onChange={(ev) => setEditState((s) => ({ ...s, region: ev.target.value }))} />
                            : <span style={{ color: e.region ? undefined : '#9ca3af' }}>{e.region ?? '—'}</span>}
                        </td>
                        <td style={{ ...td, color: '#6b7280' }}>
                          {isEditing
                            ? <input style={inp(90)} type="date" value={editState.memberSince}
                                onChange={(ev) => setEditState((s) => ({ ...s, memberSince: ev.target.value }))} />
                            : e.memberSince
                              ? new Date(e.memberSince).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' })
                              : '—'}
                        </td>
                        <td style={{ ...td, color: '#6b7280' }}>
                          {new Date(e.importedAt).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </td>
                        <td style={td}>
                          {isEditing ? (
                            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                              <button onClick={() => void saveEdit(e.id)} disabled={editState.saving}
                                style={{ padding: '3px 10px', fontSize: 12, fontWeight: 700, background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', opacity: editState.saving ? 0.6 : 1 }}>
                                {editState.saving ? '…' : 'Mentés'}
                              </button>
                              <button onClick={cancelEdit}
                                style={{ padding: '3px 10px', fontSize: 12, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' }}>
                                Mégse
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: 5 }}>
                              <button onClick={() => startEdit(e)}
                                style={{ padding: '3px 10px', fontSize: 12, background: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd', borderRadius: 4, cursor: 'pointer' }}>
                                Szerkesztés
                              </button>
                              <button onClick={() => void handleDelete(e.id, e.taxId)}
                                style={{ padding: '3px 10px', fontSize: 12, color: '#dc2626', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, cursor: 'pointer' }}>
                                Törlés
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                      {/* Address sub-row shown during editing */}
                      {isEditing && (
                        <tr style={{ background: '#eff6ff', borderBottom: '1px solid #e5e7eb' }}>
                          <td colSpan={10} style={{ ...td, paddingTop: 4, paddingBottom: 10 }}>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                              <div>
                                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Irányítószám</div>
                                <input style={inp(80)} value={editState.hqZip}
                                  onChange={(ev) => setEditState((s) => ({ ...s, hqZip: ev.target.value }))} />
                              </div>
                              <div style={{ flex: 1, minWidth: 120 }}>
                                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Város</div>
                                <input style={inp()} value={editState.hqCity}
                                  onChange={(ev) => setEditState((s) => ({ ...s, hqCity: ev.target.value }))} />
                              </div>
                              <div style={{ flex: 2, minWidth: 160 }}>
                                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 2 }}>Utca, házszám</div>
                                <input style={inp()} value={editState.hqStreet}
                                  onChange={(ev) => setEditState((s) => ({ ...s, hqStreet: ev.target.value }))} />
                              </div>
                            </div>
                            {editState.error && <div style={{ marginTop: 6, fontSize: 11, color: '#dc2626' }}>{editState.error}</div>}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
